// src/components/tickets/detail/TicketHeader.tsx
//
// The ticket screen header (Story 4.2 AC 1): question-type label as the page
// title, full question text, the status badge + route chip + flag chips (all via
// ticketDisplay.ts — the single display vocabulary), and meta (caller, assignee
// + position, born time). Unroutable tickets carry the org-admin explainer.
//
// The header's right-side actions slot holds the ticket-desk actions — Respond /
// Internal note / Escalate / Reassign / Close (Story 4.3 + 4.4) — passed in as
// `actions` by the page so the slot stays a mount point, not a data dependency.

import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { AlertTriangle } from 'lucide-react'
import { Badge } from '../../ui/Badge'
import { formatDateTime, formatRelativeTime } from '../../../utils/dateUtils'
import {
  ticketStatusBadge,
  routeChip,
  ticketFlags,
  questionTypeLabel,
  ticketBrand,
  assigneeName,
} from '../../../utils/ticketDisplay'
import type { YakTicketDetail } from '../../../types/yak'

export function TicketHeader({
  detail,
  actions,
}: {
  detail: YakTicketDetail
  actions?: ReactNode
}) {
  const { ticket, call } = detail
  const status = ticketStatusBadge(ticket.status)
  const route = routeChip(ticket.route_label)
  const flags = ticketFlags(ticket)

  // The current assignee's position rides the assignment history embed (the
  // ticket's own assignee embed carries no position). Latest matching row wins.
  const currentAssignment = [...detail.assignments]
    .reverse()
    .find(a => a.assignee_user_id === ticket.assignee_user_id)
  const position = currentAssignment?.assignee?.position ?? null

  // Caller = the call's rep (name + registered email); "—" when unrecognized.
  const rep = call?.rep ?? null
  const callerLabel = rep ? `${rep.name} · ${rep.email}` : '—'

  const born = ticket.created_at

  return (
    <div className="card p-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          {/* The one page title: the question-type label. */}
          <p className="text-sm font-semibold uppercase tracking-wide text-gray-500">
            {questionTypeLabel(ticket.question_type)}
          </p>
          <h1 className="mt-1 text-2xl font-bold text-gray-900">
            {ticket.question}
          </h1>
        </div>

        {/* Actions slot: Respond / Internal note / Escalate / Reassign / Close
            (via `actions`). Empty on closed tickets (the page passes no
            actions). */}
        <div className="shrink-0" data-testid="ticket-actions-slot">
          {actions ?? null}
        </div>
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-2">
        <Badge variant={status.variant}>{status.label}</Badge>
        <span
          className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium ${
            route.tone === 'green'
              ? 'border-green-300 text-green-700'
              : 'border-gray-300 text-gray-600'
          }`}
        >
          {route.label}
        </span>
        {flags.map(flag => (
          <Badge key={flag.key} variant="red">
            <AlertTriangle className="mr-1 h-3 w-3" aria-hidden="true" />
            {flag.label}
          </Badge>
        ))}
      </div>

      <dl className="mt-5 grid grid-cols-1 gap-x-8 gap-y-3 text-sm sm:grid-cols-2">
        <div className="flex gap-2">
          <dt className="w-24 shrink-0 font-medium text-gray-500">Brand</dt>
          <dd className="text-gray-900">{ticketBrand(ticket)}</dd>
        </div>
        <div className="flex gap-2">
          <dt className="w-24 shrink-0 font-medium text-gray-500">Caller</dt>
          <dd className="min-w-0 break-words text-gray-900">{callerLabel}</dd>
        </div>
        <div className="flex gap-2">
          <dt className="w-24 shrink-0 font-medium text-gray-500">Assignee</dt>
          <dd className="text-gray-900">
            {assigneeName(ticket)}
            {position ? (
              <span className="text-gray-500"> · {position}</span>
            ) : null}
          </dd>
        </div>
        <div className="flex gap-2">
          <dt className="w-24 shrink-0 font-medium text-gray-500">Opened</dt>
          <dd className="text-gray-900">
            {born
              ? `${formatDateTime(born)} (${formatRelativeTime(born)})`
              : '—'}
          </dd>
        </div>
        {/* Story 6.3 (FR-43): the code the rep quotes on a callback. Read-only,
            and a display handle only — nothing resolves from it. Pre-6.3 tickets
            have none; "—" rather than a blank row. The reply token is NEVER
            shown here (AD-5: it is a bearer credential, not a reference). */}
        <div className="flex gap-2">
          <dt className="w-24 shrink-0 font-medium text-gray-500">Reference</dt>
          <dd className="font-mono text-gray-900">
            {ticket.reference_code ?? '—'}
          </dd>
        </div>
      </dl>

      {ticket.unroutable ? (
        <p className="mt-4 rounded-md bg-gray-50 px-3 py-2 text-sm text-gray-600">
          Couldn&apos;t assign automatically — routed to you as org admin.{' '}
          <Link
            to="/settings/routing-rules"
            className="text-brand-600 hover:text-brand-700 hover:underline"
          >
            Settings → Routing Rules
          </Link>
        </p>
      ) : null}
    </div>
  )
}
